/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { cssMap as compiledCssMap } from '@compiled/react';
import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl';

import { cssMap, cx, jsx } from '@atlaskit/css';
import type { EmojiProvider } from '@atlaskit/emoji';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { useFlags } from '@atlaskit/flag/use-flags';
import ViewAllIcon from '@atlaskit/icon/core/align-text-left';
import PinIcon from '@atlaskit/icon/core/pin';
import PinFilledIcon from '@atlaskit/icon/core/pin-filled';
import InformationIcon from '@atlaskit/icon/core/status-information';
import { ButtonItem } from '@atlaskit/menu';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags';
import { Popup } from '@atlaskit/popup/popup';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import type { AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import { isForgeAgentByCreatorType } from '@atlaskit/rovo-agent-components/common/utils/is-forge-agent';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { MenuList as AtlasMenuList } from '@atlaskit/side-nav-items/menu-list';
import {
	MenuSection as AtlasMenuSection,
	MenuSectionHeading as AtlasMenuSectionHeading,
} from '@atlaskit/side-nav-items/menu-section';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import TooltipPrimitive from '@atlaskit/tooltip/TooltipPrimitive';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useStagingAreaActions } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import { ScopeRow } from '@atlassian/conversation-assistant-ui-components/ui/chat-conversation-list-refresh/scope-row';
import { ScopeFileBlobPreviewModal } from '@atlassian/conversation-assistant-ui-components/ui/chat-conversation-list-refresh/ScopeFileBlobPreviewModal';
import { useUpdateTime } from '@atlassian/conversation-assistant-ui-components/ui/chat-conversation-list/useUpdateTime';
import {
	ConversationListRowStatusChip,
	ConversationStatusLabel,
	shouldShowConversationTaskStatusFooter,
} from '@atlassian/conversation-assistant-ui-components/ui/conversation-status-label';
import { getConversationListRowLoadingStatus } from '@atlassian/conversation-assistant-ui-components/utils/conversation-task-status-indicator';
import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';
import { ChatListItem as SharedChatListItem } from '@atlassian/rovo-platform-ui-components/chat-list/ChatListItem';
import type { ChatListItemContainerProps } from '@atlassian/rovo-platform-ui-components/chat-list/types';
import { useSpaces, useSpacesLoading } from '@atlassian/rovo-spaces/controllers/space-store';
import type { Space } from '@atlassian/rovo-spaces/controllers/space-store/types';
import { createEmojiProvider } from '@atlassian/rovo-spaces/utils/emoji-provider';

import { COLLAPSE_ELEM_BEFORE, TESTID_PREFIX } from '../../../common/constants';
import { usePinnedConversations } from '../../../common/hooks/use-pinned-conversations';
import commonMessages from '../../../common/messages';
import type { Conversation, ConversationListProps } from '../../../common/types';
import { ButtonMenuItem } from '../../../common/ui/button-menu-item';
import { LinkMenuItem } from '../../../common/ui/link-menu-item';
import { List } from '../../../common/ui/list';
import { MenuSection } from '../../../common/ui/menu-section';
import { MenuSectionHeading } from '../../../common/ui/menu-section/menu-section-heading';
import { type ConversationGroup, groupConversationsByTime } from '../../../common/utils/date-utils';

import { AgentParticipants, getAgentFooterIdentities } from './agent-participants';
import messages from './messages';
import { MoveToSpaceSubmenu } from './move-to-space-submenu';

const styles = cssMap({
	notificationDot: {
		width: '8px',
		height: '8px',
		backgroundColor: token('color.background.selected.bold'),
		borderRadius: token('radius.full'),
	},
	statusContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '2rem',
		flexShrink: 0,
	},
	trailingStatusSlot: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '2rem',
		height: '1rem',
		flexShrink: 0,
	},
	pinnedIndicator: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '2rem',
		color: token('color.text.subtle'),
		flexShrink: 0,
	},
	pinnedIndicatorIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '16px',
		height: '16px',
		transform: 'scale(0.9375)',
		transformOrigin: 'center',
	},
	scopeOverflowCount: {
		flexShrink: '0',
		whiteSpace: 'nowrap',
		marginInlineStart: token('space.negative.025'),
	},
	firstScopeTooltipTrigger: {
		position: 'relative',
		zIndex: 1,
		display: 'inline-flex',
		alignItems: 'center',
		maxWidth: '100%',
		minWidth: '0px',
	},
	firstScopeTooltipClickableTrigger: {
		cursor: 'pointer',
	},
	scopeTooltipContent: {
		boxSizing: 'border-box',
		maxWidth: '240px',
		backgroundColor: token('color.background.neutral.bold'),
		borderRadius: token('radius.small'),
		color: token('color.text.inverse'),
		font: token('font.body.small'),
		overflowWrap: 'break-word',
		paddingBlock: token('space.050'),
		paddingInline: token('space.075'),
		wordWrap: 'break-word',
	},
	scopeNameWrapper: {
		minWidth: '0px',
		overflow: 'hidden',
	},
	scopeNameClickableWrapper: {
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	elemAfterStyles: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		height: '1.25rem',
		alignSelf: 'start',
	},
});

export type ChatListItemProps = Omit<
	ConversationListProps,
	'conversations' | 'currentConversation' | 'onConversationPinToggle' | 'onViewAllConversations'
> & {
	testIdDisambiguator: string;
	conversation: Conversation;
	isSelected: boolean;
	isADSNavigationEnabled: boolean;
	isNewAllConversationScreenEnabled: boolean;
	isSpinnerOverlayConversationIconEnabled: boolean;
	spaces: Space[];
	isLoadingSpaces: boolean;
	spaceEmojiProvider: Promise<EmojiProvider>;
	cloudId?: string;
	level?: number;
	onConversationPinToggle?: (conversation: Conversation, isPinned: boolean) => boolean;
	isPinnedChatsEnabled?: boolean;
	pinLimitError?: { maxPinnedConversations?: number };
	onDismissPinLimitError?: () => void;
	listItemRef?: React.Ref<HTMLDivElement>;
};

export const ChatListItem = ({
	testIdDisambiguator,
	conversation,
	onSelectConversation,
	onConversationRename,
	onConversationDelete,
	onConversationMoveToSpace,
	onConversationMoveToNewSpace,
	isSelected,
	level = 1,
	isADSNavigationEnabled,
	isNewAllConversationScreenEnabled,
	isSpinnerOverlayConversationIconEnabled,
	spaces,
	isLoadingSpaces,
	spaceEmojiProvider,
	cloudId,
	onConversationPinToggle,
	isPinnedChatsEnabled = false,
	pinLimitError,
	onDismissPinLimitError,
	listItemRef,
}: ChatListItemProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlags();
	const { sendAnalyticsEvent } = useAnalytics();
	const { setStagingAreaMode } = useStagingAreaActions();
	const [fileScopePreview, setFileScopePreview] = useState<{ link: string; name: string } | null>(
		null,
	);
	const [isScopeTooltipOpen, setIsScopeTooltipOpen] = useState(false);
	const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

	const relativeTime = useUpdateTime({
		isoDate: conversation.lastMessageTimestamp,
		relativeTimeStyle: 'long',
	});
	const mentionAgentsExperimentEnabled = isExperimentEnabled('rovo_chat_mention_agents');
	const agentFooterIdentities = useMemo(() => {
		if (!mentionAgentsExperimentEnabled) {
			return [];
		}
		return getAgentFooterIdentities({
			participants: conversation.participants,
			conversationAgent: conversation.agent,
		});
	}, [conversation.agent, conversation.participants, mentionAgentsExperimentEnabled]);
	const shouldUseParticipantAgentInfo = agentFooterIdentities.length > 0;
	const agentName = conversation.agent?.agent_name;
	const shouldShowAgentInfo = mentionAgentsExperimentEnabled
		? !shouldUseParticipantAgentInfo &&
			!conversation.agent?.is_default &&
			!isDefaultRovoChat({ agentId: conversation.agent?.agent_id })
		: !conversation.agent?.is_default;
	const scopes = conversation.scopes;
	const firstScope = scopes?.[0];
	const scopesOverflowCount = scopes && scopes.length > 1 ? scopes.length - 1 : 0;

	const isClickableScopeTooltipsEnabled = fg('rovo_chat_clickable_scope_tooltips');

	const showTaskStatusFooterRow = shouldShowConversationTaskStatusFooter(conversation);

	const shouldShowUnreadStatus = conversation.readStatus === false;

	const testId = `${TESTID_PREFIX}-history-item-${testIdDisambiguator}`;

	// ── Computed visual slots ─────────────────────────────────────────────

	const footerTextColor = 'color.text.subtlest';

	const handleScopeLinkClicked = useCallback(
		(variant: 'default' | 'tooltip') => {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'scopeLink',
				attributes: {
					isTooltip: variant === 'tooltip',
				},
			});
		},
		[sendAnalyticsEvent],
	);

	const handleScopeLinkHovered = useCallback(() => {
		sendAnalyticsEvent({
			action: 'hovered',
			actionSubject: 'link',
			actionSubjectId: 'scopeLink',
			attributes: {
				scopeCount: scopes?.length ?? 0,
			},
		});
	}, [sendAnalyticsEvent, scopes]);

	const handleScopeTooltipClose = useCallback(() => {
		setIsScopeTooltipOpen(false);
	}, []);

	const handleScopeTooltipTriggerClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setIsScopeTooltipOpen((isOpen) => !isOpen);
	}, []);

	const handleScopeTooltipTriggerKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLElement>) => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				event.stopPropagation();
				setIsScopeTooltipOpen((isOpen) => !isOpen);
			}
		},
		[],
	);

	const description = isNewAllConversationScreenEnabled ? (
		<Inline alignBlock="center" space="space.050">
			{showTaskStatusFooterRow && (
				<ConversationStatusLabel conversation={conversation} showStatusIcon={false} />
			)}
			{!showTaskStatusFooterRow && shouldUseParticipantAgentInfo && (
				<AgentParticipants
					identities={agentFooterIdentities}
					cloudId={cloudId}
					textColor={footerTextColor}
				/>
			)}
			{!showTaskStatusFooterRow && shouldShowAgentInfo && conversation.agent && (
				<React.Fragment>
					<AgentAvatar
						agentNamedId={conversation.agent.agent_named_id}
						agentId={conversation.agent.agent_id}
						agentIdentityAccountId={conversation.agent.identity_account_id}
						size="xxsmall"
						showBorder={false}
						isForgeAgent={
							fg('rovo_agent_support_a2a_avatar')
								? isForgeAgentByCreatorType(conversation.agent.creator_type as AgentCreatorType)
								: conversation.agent.creator_type === 'THIRD_PARTY' ||
									conversation.agent.creator_type === 'FORGE'
						}
						forgeAgentIconUrl={conversation.agent.forge_url}
					/>
					<Text size="small" weight="regular" maxLines={1} align="start" color={footerTextColor}>
						{agentName}
					</Text>
				</React.Fragment>
			)}
			{!showTaskStatusFooterRow && firstScope && (
				<React.Fragment>
					{(shouldUseParticipantAgentInfo || (shouldShowAgentInfo && conversation.agent)) && (
						<Text size="small" color={footerTextColor}>
							{formatMessage(messages.footerSeparator)}
						</Text>
					)}
					{isClickableScopeTooltipsEnabled ? (
						<Popup
							isOpen={isScopeTooltipOpen}
							onClose={handleScopeTooltipClose}
							placement="bottom-start"
							shouldRenderToParent
							content={() => (
								<TooltipPrimitive placement="bottom" role="tooltip">
									<Box xcss={styles.scopeTooltipContent}>
										<Stack space="space.100">
											{(scopes ?? []).map((scope, index) => (
												<Box key={`${scope.link ?? 'scope'}-${index}`}>
													<ScopeRow
														scope={scope}
														variant="tooltip"
														maxLines={2}
														onFilePreview={(link, name) => setFileScopePreview({ link, name })}
														onScopeLinkClick={handleScopeLinkClicked}
													/>
												</Box>
											))}
										</Stack>
									</Box>
								</TooltipPrimitive>
							)}
							trigger={(triggerProps) => (
								<Box
									as="span"
									role="button"
									tabIndex={0}
									xcss={cx(
										styles.firstScopeTooltipTrigger,
										styles.firstScopeTooltipClickableTrigger,
									)}
									{...triggerProps}
									onClick={handleScopeTooltipTriggerClick}
									onKeyDown={handleScopeTooltipTriggerKeyDown}
								>
									<Inline alignBlock="center" space="space.050">
										<Box xcss={cx(styles.scopeNameWrapper, styles.scopeNameClickableWrapper)}>
											<ScopeRow
												scope={{ ...firstScope, link: undefined }}
												variant="default"
												maxLines={1}
												onScopeLinkHover={handleScopeLinkHovered}
											/>
										</Box>
										{scopesOverflowCount > 0 && (
											<Box as="span" xcss={styles.scopeOverflowCount}>
												<Text
													as="span"
													size="small"
													weight="regular"
													align="start"
													color={footerTextColor}
												>
													{formatMessage(messages.scopesOverflowCount, {
														count: scopesOverflowCount,
													})}
												</Text>
											</Box>
										)}
									</Inline>
								</Box>
							)}
						/>
					) : (
						<Tooltip
							content={
								<Stack space="space.100">
									{(scopes ?? []).map((scope, index) => (
										<Box key={`${scope.link ?? 'scope'}-${index}`}>
											<ScopeRow
												scope={scope}
												variant="tooltip"
												maxLines={2}
												onFilePreview={(link, name) => setFileScopePreview({ link, name })}
												onScopeLinkClick={handleScopeLinkClicked}
											/>
										</Box>
									))}
								</Stack>
							}
							delay={0}
							position="bottom"
							tag="span"
						>
							{(tooltipProps) => (
								<Box as="span" xcss={styles.firstScopeTooltipTrigger} {...tooltipProps}>
									<Inline alignBlock="center" space="space.050">
										<Box xcss={styles.scopeNameWrapper}>
											<ScopeRow
												scope={firstScope}
												variant="default"
												maxLines={1}
												onFilePreview={(link, name) => setFileScopePreview({ link, name })}
												onScopeLinkClick={handleScopeLinkClicked}
												onScopeLinkHover={handleScopeLinkHovered}
											/>
										</Box>
										{scopesOverflowCount > 0 && (
											<Box as="span" xcss={styles.scopeOverflowCount}>
												<Text
													as="span"
													size="small"
													weight="regular"
													align="start"
													color={footerTextColor}
												>
													{formatMessage(messages.scopesOverflowCount, {
														count: scopesOverflowCount,
													})}
												</Text>
											</Box>
										)}
									</Inline>
								</Box>
							)}
						</Tooltip>
					)}
				</React.Fragment>
			)}
			{relativeTime && (
				<React.Fragment>
					{(shouldUseParticipantAgentInfo ||
						(shouldShowAgentInfo && conversation.agent) ||
						firstScope ||
						showTaskStatusFooterRow) && (
						<Text size="small" color={footerTextColor}>
							{formatMessage(messages.footerSeparator)}
						</Text>
					)}
					<Text size="small" weight="regular" maxLines={1} align="start" color={footerTextColor}>
						{relativeTime}
					</Text>
				</React.Fragment>
			)}
		</Inline>
	) : undefined;

	const onPinToggleClicked = useCallback(
		(event?: React.MouseEvent<HTMLElement>) => {
			event?.preventDefault();
			event?.stopPropagation();

			onConversationPinToggle?.(conversation, !conversation.isPinned);
			setIsMoreMenuOpen(false);
		},
		[conversation, onConversationPinToggle],
	);

	const pinLimitDescription =
		pinLimitError?.maxPinnedConversations !== undefined
			? formatMessage(messages.pinLimitPopupDescriptionWithLimit, {
					limit: pinLimitError.maxPinnedConversations,
				})
			: formatMessage(messages.pinLimitPopupDescription);

	useEffect(() => {
		if (!pinLimitError) {
			return;
		}

		showFlag({
			id: 'rovo-navigation.pin-limit',
			title: formatMessage(messages.pinLimitPopupTitle),
			description: pinLimitDescription,
			icon: <InformationIcon label="" color={token('color.icon.information')} />,
			isAutoDismiss: true,
			autoDismissSeconds: 5,
		});
		onDismissPinLimitError?.();
	}, [formatMessage, onDismissPinLimitError, pinLimitDescription, pinLimitError, showFlag]);

	const pinnedIndicator =
		isPinnedChatsEnabled && conversation.isPinned ? (
			<Box
				xcss={styles.pinnedIndicator}
				testId={`${TESTID_PREFIX}-history-item-pinned-indicator-${testIdDisambiguator}`}
			>
				<Box xcss={styles.pinnedIndicatorIcon}>
					<PinFilledIcon label="" color="currentColor" size="small" />
				</Box>
			</Box>
		) : null;

	const conversationListRowStatus = getConversationListRowLoadingStatus(
		conversation,
		shouldShowUnreadStatus,
	);
	const shouldShowTrailingStatusSlot =
		isNewAllConversationScreenEnabled &&
		(conversationListRowStatus !== undefined || shouldShowUnreadStatus);
	const shouldReserveTrailingStatusSlot =
		isNewAllConversationScreenEnabled &&
		(shouldShowTrailingStatusSlot || (isPinnedChatsEnabled && conversation.isPinned));

	const elemAfter = (
		<Box xcss={styles.elemAfterStyles}>
			{shouldReserveTrailingStatusSlot ? (
				<Box
					xcss={styles.statusContainer}
					testId={`${TESTID_PREFIX}-history-item-status-slot-${testIdDisambiguator}`}
				>
					<ConversationListRowStatusChip
						conversation={conversation}
						testId={
							shouldShowUnreadStatus
								? `${TESTID_PREFIX}-history-item-unread-indicator-${testIdDisambiguator}`
								: undefined
						}
					/>
				</Box>
			) : null}
			{pinnedIndicator}
		</Box>
	);

	const size = 'small';

	// ── Callbacks ─────────────────────────────────────────────────────────

	const onSelect = useCallback(
		(event: React.MouseEvent) => {
			event.preventDefault();
			onSelectConversation(conversation, isADSNavigationEnabled);
			setStagingAreaMode(undefined, undefined);
		},
		[onSelectConversation, conversation, isADSNavigationEnabled, setStagingAreaMode],
	);

	const onRename = useMemo(() => {
		if (!onConversationRename) {
			return undefined;
		}
		return async (newName: string) => {
			const result = await onConversationRename(conversation, newName);
			if (result?.hasError) {
				return { error: 'Rename failed' };
			}
			return undefined;
		};
	}, [onConversationRename, conversation]);

	const onDelete = useMemo(() => {
		if (!onConversationDelete) {
			return undefined;
		}
		return () => {
			onConversationDelete(conversation);
		};
	}, [onConversationDelete, conversation]);

	// ── More-menu items (product-specific) ────────────────────────────────

	const pinMenuItem = onConversationPinToggle ? (
		<ButtonItem
			testId={`${TESTID_PREFIX}-history-item-pin-menu-${testIdDisambiguator}`}
			iconBefore={conversation.isPinned ? <PinFilledIcon label="" /> : <PinIcon label="" />}
			onClick={() => onPinToggleClicked()}
		>
			{formatMessage(
				conversation.isPinned ? messages.moreMenuUnpinLabel : messages.moreMenuPinLabel,
			)}
		</ButtonItem>
	) : undefined;

	const moveToSpaceMenuItem =
		(onConversationMoveToSpace || onConversationMoveToNewSpace) &&
		FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false) ? (
			<MoveToSpaceSubmenu
				testIdDisambiguator={testIdDisambiguator}
				conversation={conversation}
				spaces={spaces}
				isLoadingSpaces={isLoadingSpaces}
				spaceEmojiProvider={spaceEmojiProvider}
				onConversationMoveToSpace={onConversationMoveToSpace}
				onConversationMoveToNewSpace={onConversationMoveToNewSpace}
				onClose={() => {}}
			/>
		) : undefined;

	const moreMenuItems =
		pinMenuItem || moveToSpaceMenuItem ? (
			<>
				{pinMenuItem}
				{moveToSpaceMenuItem}
			</>
		) : undefined;
	// ── After content (modals) ────────────────────────────────────────────

	const afterContent = fileScopePreview ? (
		<ScopeFileBlobPreviewModal
			isOpen
			onClose={() => setFileScopePreview(null)}
			fileUrl={fileScopePreview.link}
			fileName={fileScopePreview.name}
			formatMessage={formatMessage}
			filePreviewFallbackDescription={messages.filePreviewFallbackDescription}
			cloudId={cloudId}
		/>
	) : null;

	// ── renderContainer ───────────────────────────────────────────────────

	const renderContainer = useCallback(
		(props: ChatListItemContainerProps) => (
			<LinkMenuItem
				isADSNavigationEnabled={isADSNavigationEnabled}
				isContentTooltipDisabled
				href="#"
				testId={props.testId}
				level={level}
				onClick={props.onClick}
				listItemRef={listItemRef}
				actionsOnHover={props.actionsOnHover}
				isSelected={props.isSelected}
				isNewAllConversationScreenEnabled={isNewAllConversationScreenEnabled}
				size={props.size}
				elemBefore={COLLAPSE_ELEM_BEFORE}
				noActiveBackground
				description={isNewAllConversationScreenEnabled ? props.description : undefined}
				elemAfter={props.elemAfter}
			>
				{props.children}
			</LinkMenuItem>
		),
		[isADSNavigationEnabled, isNewAllConversationScreenEnabled, level, listItemRef],
	);

	return (
		<SharedChatListItem
			id={conversation.id}
			name={conversation.name}
			description={description}
			elemAfter={elemAfter}
			isSelected={isSelected}
			size={size}
			onSelect={onSelect}
			onRename={onRename}
			onDelete={onDelete}
			moreMenuItems={moreMenuItems}
			isMoreMenuOpen={isMoreMenuOpen}
			onMoreMenuOpenChange={setIsMoreMenuOpen}
			renderContainer={renderContainer}
			afterContent={afterContent}
			testId={testId}
			renameLabel={formatMessage(messages.moreMenuRenameLabel)}
			deleteLabel={formatMessage(messages.moreMenuDeleteLabel)}
			moreButtonLabel={formatMessage(commonMessages.moreBtnLabel)}
			renameInputLabel={formatMessage(messages.renameInputLabel)}
			confirmRenameLabel={formatMessage(messages.confirmRenameLabel)}
			cancelRenameLabel={formatMessage(messages.cancelRenameLabel)}
		/>
	);
};

const listStyles = compiledCssMap({
	noPaddingInlineStart: {
		paddingInlineStart: token('space.0'),
	},
});

export type ChatListMenuItemsProps = ConversationListProps & {
	level?: number;
	isADSNavigationEnabled: boolean;
	isNewAllConversationScreenEnabled?: boolean;
	isSpinnerOverlayConversationIconEnabled?: boolean;
	cloudId?: string;
};

const prefersReducedMotionQuery = '(prefers-reduced-motion: reduce)';
const pinReorderAnimatingAttribute = 'data-pin-reorder-animating';
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
const pinReorderAnimationTransition = `transform ${token('motion.duration.medium')} ${token(
	'motion.easing.out.practical',
)}`;

const shouldReduceMotion = () =>
	typeof window !== 'undefined' && window.matchMedia?.(prefersReducedMotionQuery).matches === true;

export const ChatListMenuItems = ({
	conversations = [],
	pinnedCountConversations,
	pinnedConversationSummary,
	currentConversation,
	onViewAllConversations,
	onConversationPinToggle,
	isADSNavigationEnabled,
	isNewAllConversationScreenEnabled = false,
	isSpinnerOverlayConversationIconEnabled = false,
	cloudId = '',
	...props
}: ChatListMenuItemsProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const isPinnedChatsEnabled = fg('rovo_chat_pinned_fe');
	const conversationRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
	const previousRowRectsForPinReorder = useRef<Map<string, DOMRect> | null>(null);
	const shouldAnimateNextPinReorder = useRef(false);
	const activePinReorderAnimationCleanups = useRef<(() => void)[]>([]);

	const clearPinReorderAnimations = useCallback(() => {
		for (const cleanup of activePinReorderAnimationCleanups.current) {
			cleanup();
		}
		activePinReorderAnimationCleanups.current = [];
	}, []);

	const captureConversationRowRects = useCallback(() => {
		const rowRects = new Map<string, DOMRect>();
		for (const [conversationId, element] of conversationRowRefs.current) {
			rowRects.set(conversationId, element.getBoundingClientRect());
		}
		return rowRects;
	}, []);

	const captureRowsForPinReorder = useCallback(() => {
		previousRowRectsForPinReorder.current = captureConversationRowRects();
		shouldAnimateNextPinReorder.current = true;
	}, [captureConversationRowRects]);

	const {
		applyOptimisticPinnedState,
		compareConversationsByPinnedState,
		handleConversationPinToggle,
		pinLimitError,
		dismissPinLimitError,
	} = usePinnedConversations({
		conversations,
		pinnedCountConversations,
		pinnedConversationSummary,
		isPinnedChatsEnabled,
		onConversationPinToggle,
		onBeforeOptimisticStateChange: captureRowsForPinReorder,
	});

	const filteredConversations = React.useMemo(() => {
		const visibleConversations = conversations.filter(
			(convo) =>
				!convo.isEmpty &&
				// Exclude space-scoped conversations from the regular list — they are
				// rendered inside `SpaceMenuItems` instead.
				(!convo.rovoSpaceId ||
					!FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)),
		);

		if (!isPinnedChatsEnabled) {
			return visibleConversations;
		}

		return visibleConversations.map((conversation) => {
			return applyOptimisticPinnedState(conversation);
		});
	}, [applyOptimisticPinnedState, conversations, isPinnedChatsEnabled]);

	const conversationsOrderedByPinnedState = React.useMemo(
		() =>
			isPinnedChatsEnabled
				? [...filteredConversations].sort(compareConversationsByPinnedState)
				: filteredConversations,
		[compareConversationsByPinnedState, filteredConversations, isPinnedChatsEnabled],
	);

	useIsomorphicLayoutEffect(() => {
		if (!shouldAnimateNextPinReorder.current) {
			return;
		}
		shouldAnimateNextPinReorder.current = false;
		const previousRowRects = previousRowRectsForPinReorder.current;
		previousRowRectsForPinReorder.current = null;

		if (!previousRowRects) {
			clearPinReorderAnimations();
			return;
		}

		clearPinReorderAnimations();

		if (shouldReduceMotion()) {
			return;
		}

		const movedRows: HTMLDivElement[] = [];

		for (const [conversationId, element] of conversationRowRefs.current) {
			const previousRect = previousRowRects.get(conversationId);
			if (!previousRect) {
				continue;
			}
			const currentRect = element.getBoundingClientRect();
			const deltaY = previousRect.top - currentRect.top;
			if (deltaY === 0) {
				continue;
			}
			element.style.transform = `translateY(${deltaY}px)`;
			element.style.transition = 'none';
			element.setAttribute(pinReorderAnimatingAttribute, 'true');
			movedRows.push(element);
		}

		const startAnimation = () => {
			activePinReorderAnimationCleanups.current = movedRows.map((element) => {
				element.style.transform = '';
				element.style.transition = pinReorderAnimationTransition;

				const cleanup = bind(element, {
					type: 'transitionend',
					listener: () => {
						element.style.transition = '';
						element.removeAttribute(pinReorderAnimatingAttribute);
						cleanup();
					},
				});

				return cleanup;
			});
		};

		if (typeof window.requestAnimationFrame === 'function') {
			window.requestAnimationFrame(startAnimation);
		} else {
			window.setTimeout(startAnimation, 0);
		}
	}, [clearPinReorderAnimations, conversationsOrderedByPinnedState]);

	useEffect(() => clearPinReorderAnimations, [clearPinReorderAnimations]);

	const groupedConversations = React.useMemo(() => {
		if (!isPinnedChatsEnabled) {
			return groupConversationsByTime(filteredConversations, formatMessage);
		}

		const pinnedConversations = filteredConversations
			.filter((conversation) => conversation.isPinned)
			.sort(compareConversationsByPinnedState);
		const unpinnedConversations = filteredConversations.filter(
			(conversation) => !conversation.isPinned,
		);
		const timeGroups = groupConversationsByTime(unpinnedConversations, formatMessage);

		if (pinnedConversations.length === 0) {
			return timeGroups;
		}

		return [
			{
				label: formatMessage(messages.pinnedConversationsLabel),
				conversations: pinnedConversations,
			},
			...timeGroups,
		];
	}, [
		compareConversationsByPinnedState,
		filteredConversations,
		formatMessage,
		isPinnedChatsEnabled,
	]);

	const onViewAllConversationsClick = useCallback(() => {
		onViewAllConversations();
	}, [onViewAllConversations]);

	const spaces = useSpaces();
	const isLoadingSpaces = useSpacesLoading();
	const spaceEmojiProvider = useMemo(() => createEmojiProvider(cloudId), [cloudId]);

	const renderConversationList = (group: ConversationGroup, groupIndex: number) => {
		const listItems = group.conversations.map((convo, i) => (
			<ChatListItem
				isADSNavigationEnabled={isADSNavigationEnabled}
				key={convo.id}
				testIdDisambiguator={`${groupIndex}-${i}`}
				conversation={convo}
				isSelected={convo.id === currentConversation?.id}
				isNewAllConversationScreenEnabled={isNewAllConversationScreenEnabled}
				isSpinnerOverlayConversationIconEnabled={isSpinnerOverlayConversationIconEnabled}
				spaces={spaces}
				isLoadingSpaces={isLoadingSpaces}
				spaceEmojiProvider={spaceEmojiProvider}
				cloudId={cloudId}
				isPinnedChatsEnabled={isPinnedChatsEnabled}
				listItemRef={(element) => {
					if (element) {
						conversationRowRefs.current.set(convo.id, element);
					} else {
						conversationRowRefs.current.delete(convo.id);
					}
				}}
				{...props}
				onConversationPinToggle={
					isPinnedChatsEnabled && onConversationPinToggle ? handleConversationPinToggle : undefined
				}
				pinLimitError={
					pinLimitError?.conversationId === convo.id
						? { maxPinnedConversations: pinLimitError.maxPinnedConversations }
						: undefined
				}
				onDismissPinLimitError={() => {
					dismissPinLimitError(convo.id);
				}}
			/>
		));

		if (isADSNavigationEnabled) {
			return (
				<AtlasMenuList testId={`${TESTID_PREFIX}-history-items-${groupIndex}`}>
					{listItems}
				</AtlasMenuList>
			);
		}

		return (
			<List
				testId={`${TESTID_PREFIX}-history-items-${groupIndex}`}
				xcss={listStyles.noPaddingInlineStart}
			>
				{listItems}
			</List>
		);
	};

	const grouped = groupedConversations.map((group, groupIndex) => {
		const conversationList = renderConversationList(group, groupIndex);
		const MenuSectionComponent = isADSNavigationEnabled ? AtlasMenuSection : MenuSection;

		return (
			<MenuSectionComponent key={group.label} isMenuListItem>
				{isADSNavigationEnabled ? (
					<AtlasMenuSectionHeading>{group.label}</AtlasMenuSectionHeading>
				) : (
					<MenuSectionHeading label={group.label}>{group.label}</MenuSectionHeading>
				)}
				{conversationList}
			</MenuSectionComponent>
		);
	});

	return (
		<>
			{isNewAllConversationScreenEnabled
				? renderConversationList({ label: '', conversations: conversationsOrderedByPinnedState }, 0)
				: grouped}
			<ButtonMenuItem
				isADSNavigationEnabled={isADSNavigationEnabled}
				elemBefore={<ViewAllIcon label="" />}
				onClick={onViewAllConversationsClick}
			>
				{formatMessage(messages.viewAllConversationsLabel)}
			</ButtonMenuItem>
		</>
	);
};
